import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { submitContactInquiry } from "@/lib/villaApi";
import { img } from "@/lib/images";

type Status = { type: "idle" | "sending" | "success" | "error"; msg: string };

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem("website_alt") as HTMLInputElement | null)?.value.trim() ?? "";
    if (honeypot) {
      setStatus({ type: "success", msg: t("form_success") });
      form.reset();
      return;
    }

    const fd = new FormData(form);
    fd.delete("website_alt");

    const body: Record<string, unknown> = {};
    fd.forEach((value, key) => {
      if (typeof value === "string") body[key] = value;
    });

    const required: Array<[string, string]> = [
      ["firstname", isDe ? "Vorname(n)" : "First name(s)"],
      ["lastname", isDe ? "Familienname(n)" : "Last name(s)"],
      ["email", isDe ? "E-Mail-Adresse" : "Email address"],
      ["phone", isDe ? "Telefon / WhatsApp" : "Phone / WhatsApp"],
    ];
    for (const [key, label] of required) {
      if (!String(body[key] ?? "").trim()) {
        setStatus({
          type: "error",
          msg: isDe
            ? `Bitte füllen Sie das Pflichtfeld „${label}" aus.`
            : `Please fill in the required field "${label}".`,
        });
        return;
      }
    }

    setStatus({ type: "sending", msg: "" });
    try {
      const { error } = await submitContactInquiry({
        firstname: String(body.firstname ?? ""),
        lastname: String(body.lastname ?? "").toUpperCase(),
        email: String(body.email ?? ""),
        phone: (body.phone as string) || null,
        subject: (body.subject as string) || null,
        message: (body.message as string) || null,
        lang: isDe ? "de" : "en",
      });

      if (error) {
        setStatus({ type: "error", msg: error.message || t("form_error") });
        return;
      }

      setStatus({ type: "success", msg: t("form_success") });
      form.reset();
    } catch {
      setStatus({ type: "error", msg: t("form_error") });
    }
  };

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("contact_title")}
          title={t("contact_form_title")}
          subtitle={t("contact_form_sub")}
          image={img("contact/header-contact-01.jpg")}
          imageAlt="Contact Bali VW Tour"
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">— {t("contact_title")} —</div>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 leading-[1.12] tracking-tight mb-6">
                {t("contact_info_title")}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-8">
                {t("contact_intro")}
              </p>
              <div className="space-y-4">
                <a href={`mailto:${t("contact_email")}`} className="flex items-center gap-4 group cursor-pointer">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 group-hover:bg-blue-700 group-hover:text-background-50 transition-colors">
                    <i className="ri-mail-line text-xl"></i>
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-foreground-500">{t("contact_label_email")}</div>
                    <div className="font-medium text-foreground-900">{t("contact_email")}</div>
                  </div>
                </a>
                <a href={`tel:${t("contact_phone")}`} className="flex items-center gap-4 group cursor-pointer">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-600 group-hover:text-background-50 transition-colors">
                    <i className="ri-whatsapp-line text-xl"></i>
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-foreground-500">{t("contact_label_phone")}</div>
                    <div className="font-medium text-foreground-900">{t("contact_phone")}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-map-pin-2-line text-xl"></i>
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-foreground-500">{t("contact_label_address")}</div>
                    <div className="font-medium text-foreground-900">{t("contact_address")}</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 rounded-lg overflow-hidden border border-background-200 h-[260px]">
                <iframe
                  title="Bali VW Tour Location"
                  src="https://www.google.com/maps?q=Jalan%20Ganetri%209%20D%2C%20Gatot%20Subroto%20Timur%2C%20Denpasar%2080235%2C%20Bali%2C%20Indonesia&z=17&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                ></iframe>
              </div>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-5">
                  <p className="text-sm text-foreground-600">
                    {isDe
                      ? <>Bitte bei zwei verschiedenen Namen immer beide angeben.<br />Max / Erika&nbsp;&nbsp;&nbsp;&nbsp;MUSTERMANN / MÜLLER</>
                      : <>If there are two different names, please always provide both.<br />John / Jane&nbsp;&nbsp;&nbsp;&nbsp;DOE / SMITH</>}
                  </p>
                  <div>
                    <label htmlFor="contact_firstname" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t("form_firstname")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact_firstname"
                      name="firstname"
                      type="text"
                      required
                      placeholder={t("form_firstname_ph")}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact_lastname" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t("form_lastname")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact_lastname"
                      name="lastname"
                      type="text"
                      required
                      placeholder={t("form_lastname_ph")}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 uppercase"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t("form_email")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact_email"
                      name="email"
                      type="email"
                      required
                      placeholder={t("form_email_ph")}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact_phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t("form_phone")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact_phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder={t("form_phone_ph")}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t("form_subject")}
                    </label>
                    <input
                      id="contact_subject"
                      name="subject"
                      type="text"
                      placeholder={t("form_subject_ph")}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t("form_message")}
                    </label>
                    <textarea
                      id="contact_message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder={t("form_message_ph")}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status.type === "sending"}
                    className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-6 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className="ri-send-plane-line"></i>
                    {status.type === "sending" ? t("form_sending") : t("form_submit")}
                  </button>

                  {status.type === "success" && (
                    <div className="text-sm font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
                      {status.msg}
                    </div>
                  )}
                  {status.type === "error" && (
                    <div className="text-sm font-medium text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3">
                      {status.msg}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}